import React, { useState } from "react";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setShowSuccess(false);
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Enter your last name.";
    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.subject) nextErrors.subject = "Choose a subject.";
    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please include at least 10 characters.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      document.getElementById(Object.keys(nextErrors)[0])?.focus();
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    window.setTimeout(() => {
      setFormData(emptyForm);
      setIsSubmitting(false);
      setShowSuccess(true);
    }, 700);
  };

  const contact = siteConfig.contact || {};
  const address = contact.address || contact.location;

  return (
    <main className="contact-page">
      <header className="contact-page__header">
        <p className="contact-page__eyebrow">Customer care</p>
        <h1>Let’s talk.</h1>
        <p>
          Questions about an order, fit, or the collection? Send us a note and
          our small team will get back to you.
        </p>
      </header>

      <div className="contact-page__layout">
        <aside className="contact-page__details" aria-label="Contact details">
          <div>
            <p className="contact-page__label">Email</p>
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
          </div>
          {contact.phone && (
            <div>
              <p className="contact-page__label">Phone</p>
              <a href={`tel:${contact.phone.replace(/[^+\d]/g, "")}`}>
                {contact.phone}
              </a>
            </div>
          )}
          {address && (
            <div>
              <p className="contact-page__label">Studio</p>
              <address>{address}</address>
            </div>
          )}
          <div>
            <p className="contact-page__label">Hours</p>
            <p>Monday–Friday, 9am–5pm ET</p>
            <p>Replies typically arrive within two business days.</p>
          </div>
        </aside>

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          {showSuccess && (
            <div className="contact-form__success" role="status" tabIndex="-1">
              <span aria-hidden="true">✓</span>
              Thanks for reaching out. Your message has been received.
            </div>
          )}

          <div className="contact-form__row">
            <div className="contact-form__field">
              <label htmlFor="firstName">First name</label>
              <input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                autoComplete="given-name"
                aria-invalid={Boolean(errors.firstName)}
                aria-describedby={errors.firstName ? "firstName-error" : undefined}
              />
              {errors.firstName && (
                <p id="firstName-error" className="contact-form__error">
                  {errors.firstName}
                </p>
              )}
            </div>
            <div className="contact-form__field">
              <label htmlFor="lastName">Last name</label>
              <input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                autoComplete="family-name"
                aria-invalid={Boolean(errors.lastName)}
                aria-describedby={errors.lastName ? "lastName-error" : undefined}
              />
              {errors.lastName && (
                <p id="lastName-error" className="contact-form__error">
                  {errors.lastName}
                </p>
              )}
            </div>
          </div>

          <div className="contact-form__row">
            <div className="contact-form__field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <p id="email-error" className="contact-form__error">
                  {errors.email}
                </p>
              )}
            </div>
            <div className="contact-form__field">
              <label htmlFor="phone">
                Phone <span>(optional)</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                autoComplete="tel"
              />
            </div>
          </div>

          <div className="contact-form__field">
            <label htmlFor="subject">Subject</label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              aria-invalid={Boolean(errors.subject)}
              aria-describedby={errors.subject ? "subject-error" : undefined}
            >
              <option value="">Choose one</option>
              <option value="order">Order support</option>
              <option value="product">Product and sizing</option>
              <option value="shipping">Shipping and returns</option>
              <option value="wholesale">Wholesale and press</option>
              <option value="other">Something else</option>
            </select>
            {errors.subject && (
              <p id="subject-error" className="contact-form__error">
                {errors.subject}
              </p>
            )}
          </div>

          <div className="contact-form__field">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              rows="6"
              value={formData.message}
              onChange={handleInputChange}
              aria-invalid={Boolean(errors.message)}
              aria-describedby={errors.message ? "message-error" : undefined}
            />
            {errors.message && (
              <p id="message-error" className="contact-form__error">
                {errors.message}
              </p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending…" : "Send message"}
          </button>
          <p className="contact-form__privacy">
            By submitting this form, you agree that we may use your details to
            respond to your request.
          </p>
        </form>
      </div>
    </main>
  );
}

export default Contact;
